import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isMachineViewer } from '../utils/homeRoute';

type Perm = 'canView' | 'canCreate' | 'canUpdate' | 'canDelete';

interface ProtectedModuleProps {
  children: React.ReactNode;
  moduleName: string;
  // A single permission, or a list — access is granted if the user has ANY of them.
  // e.g. ['canView', 'canCreate'] lets create-only users reach a page that also serves viewers.
  permission?: Perm | Perm[];
  // Roles that are always allowed regardless of module permissions. Use for pages that
  // certain roles should never be locked out of (e.g. the main dashboard for ADMIN).
  allowRoles?: string[];
}

const ProtectedModule: React.FC<ProtectedModuleProps> = ({
  children,
  moduleName,
  permission = 'canView',
  allowRoles
}) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admin has access to everything
  if (user.role === 'SUPERADMIN') {
    return <>{children}</>;
  }

  // Machine-viewer logins can always reach the Machines page (their only page),
  // regardless of which module permissions were checked when they were created.
  if (moduleName === 'machines' && isMachineViewer(user)) {
    return <>{children}</>;
  }

  // Resellers can always reach Company Management to manage their own companies.
  if (moduleName === 'company-management' && (user as any).isReseller) {
    return <>{children}</>;
  }

  // Roles explicitly allowed for this page bypass the module-permission check.
  if (allowRoles && allowRoles.includes(user.role)) {
    return <>{children}</>;
  }

  const perms: Perm[] = Array.isArray(permission) ? permission : [permission];
  const rowGrants = (p: any) => p?.module?.name === moduleName && perms.some((perm) => p[perm]);

  // Check if user has permission for this module (any of `perms`).
  // First check role permissions, then fallback to direct permissions.
  let hasPermission = false;

  if (user.userRole?.permissions) {
    hasPermission = user.userRole.permissions.some(rowGrants);
  }

  if (!hasPermission && user.permissions) {
    hasPermission = user.permissions.some(rowGrants);
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">
            You don't have {perms.join(' / ')} permission for the {moduleName} module.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedModule;

